import { useCallback, useEffect, useRef } from 'react';

interface OverlayHistoryState {
  mhOverlay?: boolean;
}

/**
 * Make the browser **Back** button close an open overlay (modal/dialog) instead
 * of navigating away from the page underneath it.
 *
 * How it works:
 * - When `isOpen` flips to true we push one throwaway ("sentinel") history entry
 *   that keeps the current URL. A guard on `history.state?.mhOverlay` prevents
 *   React 18 StrictMode's dev-only double-invoke from stacking two sentinels.
 * - Pressing Back pops that entry, fires `popstate`, and we translate it into an
 *   `onClose()` call — so the user stays exactly where they were (same scroll,
 *   same list) with the overlay dismissed.
 * - Every other close affordance (X button, Escape, backdrop click) should call
 *   the returned `requestClose` instead of `onClose` directly. `requestClose`
 *   simply runs `history.back()`, which pops the sentinel and reaches `onClose`
 *   through the same `popstate` path. Routing *all* closes through `history.back()`
 *   keeps the history stack balanced without a cleanup-time `history.back()`
 *   (which is unsafe under StrictMode's mount → unmount → mount cycle).
 *
 * In-modal links that genuinely navigate elsewhere (e.g. to /venues/:id) should
 * keep calling `onClose` directly and let the navigation proceed; the sentinel
 * harmlessly dangles under the new entry (bounded to one by the push guard).
 *
 * @param isOpen  whether the overlay is currently open.
 * @param onClose callback that actually dismisses the overlay (e.g. clears the
 *                selected item). Kept in a ref so the effect never re-subscribes.
 * @returns `requestClose` — invoke it from the overlay's own close controls.
 */
export function useModalHistory(isOpen: boolean, onClose: () => void): () => void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;

    const state = window.history.state as OverlayHistoryState | null;
    if (!state?.mhOverlay) {
      window.history.pushState({ ...state, mhOverlay: true }, '');
    }

    const onPop = () => onCloseRef.current();
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [isOpen]);

  return useCallback(() => {
    const state = window.history.state as OverlayHistoryState | null;
    if (state?.mhOverlay) {
      // Pop our sentinel; the resulting `popstate` runs `onClose`.
      window.history.back();
    } else {
      onCloseRef.current();
    }
  }, []);
}
